import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Rss } from "lucide-react";
import Seo from "@/components/Seo";
import { SectionTag } from "@/components/Motion";
import NotFound from "@/pages/NotFound";
import POSTS from "@/data/blog/generated/index.json";
import { BLOG_PER_PAGE, blogPageCount, blogPagePath } from "@/data/blog/config";
import { COMPANY } from "@/data/company";
import { formatDateFr } from "@/lib/format";
import { hy } from "@/lib/hyphenate";

const SITE = COMPANY.url;

export default function BlogIndex() {
  const { page: pageParam } = useParams();
  const page = pageParam === undefined ? 1 : Number(pageParam);
  const pages = blogPageCount(POSTS.length);
  if (!Number.isInteger(page) || page < 1 || page > pages || pageParam === "1") return <NotFound />;

  const posts = POSTS.slice((page - 1) * BLOG_PER_PAGE, page * BLOG_PER_PAGE);
  const path = blogPagePath(page);
  const title = page === 1
    ? "Blog assurance artisans : actualités décennale et RC Pro"
    : `Blog assurance artisans : actualités (page ${page})`;
  const description = page === 1
    ? "Actualités de l’assurance des artisans du bâtiment : décennale, RC Pro, réglementation, jurisprudence et marché. Des articles sourcés et datés."
    : `Page ${page} des actualités de l’assurance des artisans du bâtiment : décennale, RC Pro, réglementation, jurisprudence et marché.`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Blog",
      "@id": `${SITE}/blog#blog`,
      name: "Blog Décennale Artisans",
      url: `${SITE}/blog`,
      inLanguage: "fr-FR",
      publisher: { "@id": `${SITE}/#organization` },
      blogPost: posts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.h1,
        url: `${SITE}/blog/${p.slug}`,
        datePublished: p.date,
        dateModified: p.updated,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
      ],
    },
  ];

  return (
    <div data-testid="blog-page" className="mx-auto max-w-[1200px] px-5 sm:px-8 py-14 lg:py-20">
      <Seo title={title} description={description} path={path} jsonLd={jsonLd} />
      <nav aria-label="Fil d’Ariane" className="text-sm mb-8 flex flex-wrap gap-2">
        <Link to="/" className="underline">Accueil</Link><span aria-hidden="true">/</span><span>Blog</span>
      </nav>
      <SectionTag className="mb-4">Veille assurance BTP</SectionTag>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tighter leading-[0.95] mb-6">
        {hy("Le blog de l’assurance")} <span className="text-accent">{hy("des artisans.")}</span>
      </h1>
      <p className="max-w-3xl text-base sm:text-lg leading-relaxed text-ink/80">
        Réglementation, jurisprudence, marché de l’assurance construction : les évolutions qui
        comptent pour les artisans du bâtiment, expliquées simplement et toujours reliées à leurs
        sources officielles.
        {page > 1 && ` Page ${page} sur ${pages}.`}
      </p>
      <p className="mt-4">
        <a href="/blog/rss.xml" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] hover:text-accent">
          <Rss className="h-4 w-4" aria-hidden="true" /> Flux RSS
        </a>
      </p>

      {posts.length === 0 ? (
        <p className="mt-12 border-2 border-ink bg-white p-8">Les premiers articles arrivent très bientôt.</p>
      ) : (
        <ul className="mt-12 grid gap-5">
          {posts.map((post) => (
            <li key={post.slug}>
              <article className="group relative border-2 border-ink bg-white p-6 sm:p-8 transition-colors hover:border-accent">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/65">
                  <span className="text-accent">{post.category}</span> · <time dateTime={post.date}>{formatDateFr(post.date)}</time> · {post.readingTime} min
                </p>
                <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight">
                  <Link to={`/blog/${post.slug}`} className="after:absolute after:inset-0 group-hover:text-accent">
                    {post.h1}
                  </Link>
                </h2>
                <p className="mt-3 max-w-3xl leading-relaxed text-ink/75">{post.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold" aria-hidden="true">
                  Lire l’article <ArrowUpRight className="h-4 w-4" />
                </span>
              </article>
            </li>
          ))}
        </ul>
      )}

      {pages > 1 && (
        <nav aria-label="Pages du blog" className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t-2 border-ink pt-6">
          {page > 1 ? (
            <Link to={blogPagePath(page - 1)} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] hover:text-accent">
              <ArrowLeft className="h-4 w-4" /> Articles plus récents
            </Link>
          ) : <span />}
          <ol className="flex flex-wrap gap-2">
            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
              <li key={n}>
                {n === page ? (
                  <span aria-current="page" className="inline-block border-2 border-ink bg-ink px-3 py-1.5 font-mono text-xs text-paper">{n}</span>
                ) : (
                  <Link to={blogPagePath(n)} className="inline-block border-2 border-ink px-3 py-1.5 font-mono text-xs hover:bg-ink hover:text-paper">{n}</Link>
                )}
              </li>
            ))}
          </ol>
          {page < pages ? (
            <Link to={blogPagePath(page + 1)} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] hover:text-accent">
              Articles plus anciens <ArrowRight className="h-4 w-4" />
            </Link>
          ) : <span />}
        </nav>
      )}

      <section className="mt-16 flex flex-wrap items-center justify-between gap-6 border-2 border-ink bg-accent p-8 text-paper sm:p-10">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tighter">Besoin d’un devis ?</h2>
          <p className="mt-2 max-w-xl text-sm text-paper">RC Pro et décennale étudiées en une seule demande, gratuite et sans engagement.</p>
        </div>
        <Link to="/devis" className="inline-flex items-center gap-2 border-2 border-night bg-night px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-transparent hover:text-night">
          Demander un devis <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    </div>
  );
}
